/**
 * AcceptedJobSheet — pro-side confirmation slide-up the moment a shared-service
 * request becomes theirs (they accepted it, or the customer accepted their counter).
 * Shows the trip summary with one big button to jump into the job / ride.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Navigation, Clock } from "lucide-react";
import { API, useAuth } from "@/App";
import PersistentSheet from "@/components/requests/PersistentSheet";
import { SERVICE_TONES, SERVICE_LABELS, serviceToneKey } from "@/lib/serviceTones";
import { serviceKind, customerNoun } from "@/lib/cancelReasons";
import { tripFare } from "@/utils/tripFare";
import { playSoftChime } from "@/lib/chime";
import { buildNavUrl } from "@/utils/navLauncher";

const SEEN_KEY = "yorm:accepted-seen";
const seen = () => { try { return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); } };
const markSeen = (id) => { try { const s = seen(); s.add(id); sessionStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-50))); } catch { /* private mode */ } };

const isRide = (b) => ["ride", "van"].includes(serviceKind(b));
const laundryLeg = (b) => (b?.service_flavor === "laundry" ? (b.laundry_direction === "dropoff" ? "Collect the clean clothes at the laundry, then deliver to the customer." : "Collect the laundry and payment at the customer, then drop off at the shop.") : null);

export default function AcceptedJobSheet({ demo }) {
  const { token, user } = useAuth() || {};
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [trip, setTrip] = useState(demo || null);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (!token || user?.role !== "artisan") return undefined;
    const show = (b) => {
      if (!b?.id || b.artisan_user_id !== user.id || b.status !== "accepted") return;
      if (seen().has(b.id)) return;
      markSeen(b.id);
      playSoftChime();
      setTrip({ ...b, _onPage: pathRef.current.includes(b.id) });
    };
    const onAccepted = (ev) => show(ev?.detail?.booking);
    const onUpdated = async (ev) => {
      const d = ev?.detail || {};
      if (!d.booking_id || !["rideshare_accepted", "offer_accepted"].includes(String(d.type || ""))) return;
      if (seen().has(d.booking_id)) return;
      try {
        const { data } = await axios.get(`${API}/rideshare/bookings/${d.booking_id}?token=${token}`);
        show(data);
      } catch { /* not mine / gone */ }
    };
    window.addEventListener("yorm:job-accepted", onAccepted);
    window.addEventListener("yorm:booking-updated", onUpdated);
    return () => {
      window.removeEventListener("yorm:job-accepted", onAccepted);
      window.removeEventListener("yorm:booking-updated", onUpdated);
    };
  }, [token, user?.id, user?.role]);

  if (!trip) return null;
  const tone = SERVICE_TONES[serviceToneKey(trip)];
  const label = SERVICE_LABELS[serviceToneKey(trip)] || "Trip";
  const ride = isRide(trip);
  const fare = tripFare(trip);
  const km = trip.estimated_distance_km != null ? Number(trip.estimated_distance_km).toFixed(1) : null;
  const who = trip.customer_name || `Your ${customerNoun(trip)}`;
  const go = () => { const id = trip.id; setTrip(null); navigate(`/professional/trip/${id}`); };
  // Already on this trip's page (accepted from there): the button opens turn-by-turn to the pickup instead.
  const navUrl = trip._onPage ? buildNavUrl(trip.pickup_lat, trip.pickup_lng, "auto", [], trip.pickup_address || "") : null;
  const primary = () => {
    if (!trip._onPage) return go();
    setTrip(null);
    if (navUrl) window.open(navUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <PersistentSheet open title={ride ? "Trip accepted" : "Job accepted"} icon={Check} testId="accepted-job-sheet" className="pay-glass">
      <div className="space-y-4" style={{ "--svc": tone.main, "--svc-dark": tone.dark }}>
        <div className="flex flex-col items-center text-center pt-1">
          <span className="h-14 w-14 rounded-full flex items-center justify-center text-white shadow-md animate-in zoom-in-50 duration-300" style={{ background: tone.main }}>
            <Check size={28} strokeWidth={3} />
          </span>
          <p className="mt-3 text-[17px] font-semibold text-[#222]" data-testid="accepted-job-headline">
            {label} {ride ? "trip" : "job"} with {who}
          </p>
          <p className="text-[13px] text-gray-500" data-testid="accepted-job-sub">
            {laundryLeg(trip) || (ride ? "Head to the pickup now — the rider has been told you're on the way." : "The customer has been told you're on the way.")}
          </p>
        </div>

        <div className="rounded-2xl bg-white/80 p-4 space-y-3" data-testid="accepted-job-summary">
          <div className="flex gap-3">
            <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-white" style={{ background: tone.main }}><MapPin size={13} /></span>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Pickup</p>
              <p className="text-[14px] text-[#222] leading-snug" data-testid="accepted-job-pickup">{trip.pickup_address || "Pinned location"}</p>
            </div>
          </div>
          {trip.dropoff_address && (
            <div className="flex gap-3">
              <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#222] flex items-center justify-center text-white"><Navigation size={13} /></span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">Drop-off</p>
                <p className="text-[14px] text-[#222] leading-snug" data-testid="accepted-job-dropoff">{trip.dropoff_address}</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between pt-1 border-t border-gray-100">
            <span className="text-[13px] text-gray-500 inline-flex items-center gap-1"><Clock size={13} />{km ? `${km} km` : "Distance on the map"}</span>
            {fare != null && <span className="text-[16px] font-bold text-[#222]" data-testid="accepted-job-fare">GHS {fare.toFixed(2)}</span>}
          </div>
        </div>

        <Button onClick={primary} className="w-full h-12 rounded-xl text-white text-[16px] font-semibold bg-[var(--svc)] hover:bg-[var(--svc-dark)]" data-testid="accepted-job-go-btn">
          <Navigation size={18} className="mr-2" /> {trip._onPage ? (navUrl ? "Navigate to pickup" : "Got it") : ride ? "Go to ride" : "Go to job"}
        </Button>
        <button type="button" onClick={() => setTrip(null)} className="w-full text-[15px] font-medium text-gray-500 pb-1" data-testid="accepted-job-later-btn">
          {trip._onPage ? "Close" : "Later — it's in my Bookings"}
        </button>
      </div>
    </PersistentSheet>
  );
}
